/**
 * Data grid wrapper — QA convention (see project testing docs):
 *
 * - **Container:** pass `tableDataTestId` (e.g. `contractors-onboarding-list-table`).
 * - **Rows:** pass `rowDataTestIdPrefix` (e.g. `contractors-onboarding-list`). Each row
 *   renders as `data-testid="\`${prefix}-row-${rowId}\`"` where `rowId` is the grid's
 *   stable row id — set via `getRowId` to the entity's id (`row.id`, `masterContractorId`, etc.),
 *   not an arbitrary UUID unless the backend id is a UUID.
 */
import {
  DataGridPro,
  DataGridProProps,
  GridColumnOrderChangeParams,
  GridPinnedColumnFields,
  GridValidRowModel,
} from '@mui/x-data-grid-pro'
import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro'
import { GridRow, GridRowProps } from '@mui/x-data-grid-pro'
import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import GlobalStyles from '@mui/material/GlobalStyles'
import { Columns3Minor } from '@shopify/polaris-icons'
import { GridTableFooter } from 'src/components/shared/grid/GridTableFooter'
import { GridColumnMenuPinningItemWithUnpinIcon } from 'src/components/shared/grid/grid-column-menu/GridColumnMenuPinningItemWithUnpinIcon'
import { GridColumnMenuSortItemWithUnsortIcon } from 'src/components/shared/grid/grid-column-menu/GridColumnMenuSortItemWithUnsortIcon'

const PinIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.33255 16.6666L6.66589 13.3333M3.71772 10.3851L9.61405 16.2814C10.5236 17.191 12.077 16.771 12.4044 15.5271L13.5573 11.1459C13.6793 10.6824 13.995 10.2937 14.4237 10.0794L16.681 8.95077C17.6985 8.44202 17.9186 7.08591 17.1141 6.28151L13.7176 2.88504C12.9132 2.08063 11.5571 2.3007 11.0484 3.31819L9.9198 5.57542C9.70547 6.00418 9.3168 6.31986 8.85322 6.44186L4.47208 7.59481C3.22811 7.92216 2.80815 9.47552 3.71772 10.3851Z"
      stroke="black"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const HideIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8.95117 4.23845C12.2359 3.78917 15.6593 5.45848 17.914 9.24644C18.1905 9.71102 18.1907 10.2893 17.9142 10.7539C17.6025 11.2774 17.2686 11.7605 16.9158 12.2031"
      stroke="black"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.08861 1.91107C2.76317 1.58563 2.23553 1.58563 1.91009 1.91107C1.58466 2.23651 1.58466 2.76414 1.91009 3.08958L3.08861 1.91107ZM16.9101 18.0896C17.2355 18.415 17.7632 18.415 18.0886 18.0896C18.414 17.7642 18.414 17.2365 18.0886 16.9111L16.9101 18.0896ZM1.91009 3.08958L16.9101 18.0896L18.0886 16.9111L3.08861 1.91107L1.91009 3.08958Z"
      fill="black"
    />
    <path
      d="M8.60993 9.079C8.86477 8.69575 8.76068 8.17848 8.37743 7.92365C7.99416 7.66884 7.47689 7.77295 7.22207 8.1562L8.60993 9.079ZM11.8432 12.7773C12.2264 12.5225 12.3305 12.0052 12.0757 11.6219C11.8208 11.2387 11.3036 11.1346 10.9203 11.3894L11.8432 12.7773ZM9.99935 11.6667C9.07885 11.6667 8.33268 10.9205 8.33268 10H6.66602C6.66602 11.841 8.1584 13.3333 9.99935 13.3333V11.6667ZM8.33268 10C8.33268 9.65808 8.43477 9.3425 8.60993 9.079L7.22207 8.1562C6.87072 8.68466 6.66602 9.31966 6.66602 10H8.33268ZM10.9203 11.3894C10.6568 11.5646 10.3413 11.6667 9.99935 11.6667V13.3333C10.6797 13.3333 11.3148 13.1287 11.8432 12.7773L10.9203 11.3894Z"
      fill="black"
    />
    <path
      d="M5.10765 5.83301C3.97849 6.66166 2.94698 7.79932 2.08593 9.24592C1.80939 9.71059 1.81015 10.2903 2.08673 10.7548C5.04461 15.7231 10.0133 17.0458 14.0419 14.7231"
      stroke="black"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ManageColumnsIcon = () => <Columns3Minor width={20} height={20} fill="black" />

const SortAscIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M7 14V4M7 4L4 7M7 4L10 7"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M13 8H17M13 11H16M13 14H15" stroke="black" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

const SortDescIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M7 6V16M7 16L4 13M7 16L10 13"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M13 8H17M13 11H16M13 14H15" stroke="black" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

const GRID_COLUMN_MENU_STYLES = {
  '.grid-column-menu-item.MuiDataGrid-menuList': {
    width: 200,
    minWidth: 200,
  },
  '.grid-column-menu-item .MuiMenuItem-root': {
    fontSize: 14,
    padding: '8px 12px',
    color: '#000',
  },
  '.grid-column-menu-item .MuiListItemText-primary': {
    fontSize: 14,
    color: '#000',
  },
  '.grid-column-menu-item .MuiMenuItem-root .MuiListItemIcon-root': {
    minWidth: 0,
    marginRight: 8,
  },
  '.grid-column-menu-item .MuiMenuItem-root + .MuiDivider-root': {
    marginTop: 0,
    marginBottom: 0,
  },
}

const DEFAULT_COLUMN_MIN_WIDTH = 120

/**
 * DataGrid draws a primary-coloured outline on the focused cell, and because it focuses cells on
 * pointer-down that ring appears on every click — reading as a stuck selection box on tables where
 * clicking a row means nothing.
 *
 * `:focus-visible` is deliberately left alone: it is how the browser distinguishes keyboard focus
 * from a mouse click, so arrow-key navigation of the grid keeps a visible focus indicator. Never
 * collapse these into a blanket `outline: none` — that would leave keyboard users with no idea
 * where they are.
 */
const GRID_FOCUS_RING_STYLES = {
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-cell:focus-visible, & .MuiDataGrid-columnHeader:focus-visible': {
    outline: '2px solid var(--mui-primary-500)',
    outlineOffset: '-2px',
  },
}

function createGridRowWithTestId(prefix: string) {
  return React.forwardRef<HTMLDivElement, GridRowProps>(function GridRowWithTestId(props, ref) {
    return <GridRow ref={ref} {...props} data-testid={`${prefix}-row-${props.rowId}`} />
  })
}

interface Props<R extends GridValidRowModel = any> extends DataGridProProps<R> {
  isLoading?: boolean
  EmptyElement?: React.JSXElementConstructor<any>
  className?: any
  /**
   * The grid is the top of its card — nothing (tabs, filter row) sits flush above it — so its
   * top corners are rounded like the bottom ones. Off by default: most grids hang under a
   * toolbar that owns the card's top edge, and the theme squares the grid's top for that.
   */
  isStandalone?: boolean
  /** Container for QA: [page]-[section]-table */
  tableDataTestId?: string
  /** Row prefix for QA: rows become `${rowDataTestIdPrefix}-row-${rowId}` */
  rowDataTestIdPrefix?: string
  columnVisibilityModel?: GridColumnVisibilityModel
  pinnedColumns?: GridPinnedColumnFields
  onColumnVisibilityModelChange?: DataGridProProps<R>['onColumnVisibilityModelChange']
  onPinnedColumnsChange?: DataGridProProps<R>['onPinnedColumnsChange']
  onColumnOrderChange?: (params: GridColumnOrderChangeParams) => void
  onExportClick?: () => void
  /**
   * Grow to fill the remaining height of a flex-column parent instead of the fixed
   * viewport-stepped heights. The parent must be a flex column with a definite height (e.g. a
   * page that is itself `flex-1 min-h-0` inside the shell). No height floor: the grid shrinks
   * with the viewport so only its own body ever scrolls — a floor would push the page into a
   * second scrollbar on short screens.
   */
  fillHeight?: boolean
  /**
   * Size to the rows instead of a fixed height, so the grid has no inner scroller of its own.
   *
   * Without this a short grid still gets the stepped height below, and its virtual scroller
   * swallows the wheel over that whole box — the page stops scrolling while the pointer is
   * anywhere inside it, with no rows to scroll in exchange. Use for a small, embedded table
   * (a detail-page history); leave off for a paginated list, which wants a stable height.
   * Mutually exclusive with `fillHeight`; that one wins if both are passed.
   */
  autoHeight?: boolean
}

const GridTableComponent = <R extends GridValidRowModel = any>({
  rows,
  columns,
  isLoading,
  paginationModel,
  pageSizeOptions,
  className,
  isStandalone = false,
  EmptyElement = () => null,
  tableDataTestId,
  rowDataTestIdPrefix,
  columnVisibilityModel,
  pinnedColumns,
  onColumnVisibilityModelChange,
  onPinnedColumnsChange,
  onColumnOrderChange,
  onExportClick,
  fillHeight,
  autoHeight,
  ...props
}: Props<R>) => {
  const RowSlot = useMemo(
    () => (rowDataTestIdPrefix ? createGridRowWithTestId(rowDataTestIdPrefix) : undefined),
    [rowDataTestIdPrefix],
  )

  // Every column gets a floor so narrow viewports scroll rather than crush text — except a
  // column that asks for LESS than the floor (the 68px actions column): there the fixed width
  // is the intent, and a 120px floor would silently widen it.
  const columnsWithMinWidth = useMemo(() => {
    return columns.map((column) => {
      const minWidth =
        column.width !== undefined && column.width < DEFAULT_COLUMN_MIN_WIDTH
          ? column.width
          : DEFAULT_COLUMN_MIN_WIDTH

      return { minWidth, ...column }
    })
  }, [columns])

  return (
    <div
      data-testid={tableDataTestId}
      css={[
        tw`w-full`,
        fillHeight ? tw`min-h-0 flex-1` : null,
        // MUI X puts `overscroll-behavior: none` on the virtual scroller, which is still a
        // (horizontal) scroll container under `autoHeight`. That stops a wheel over the grid from
        // chaining to the page or modal around it, so scrolling sticks once the pointer reaches
        // the table. An autoHeight grid has no vertical scroll of its own, so let it chain. The
        // extra `.MuiDataGrid-root` outranks MUI's own two-class rule, which is injected later.
        !fillHeight && autoHeight
          ? tw`h-auto [& .MuiDataGrid-root .MuiDataGrid-virtualScroller]:overscroll-auto`
          : null,
        !fillHeight && !autoHeight
          ? [
              tw`h-[400px]`,
              {
                '@media (min-height: 830px)': tw`h-[520px]`,
                '@media (min-height: 1000px)': tw`h-[700px]`,
                '@media (min-height: 1440px)': tw`h-[1100px]`,
                '@media (min-height: 2000px)': tw`h-[1500px]`,
              },
            ]
          : null,
        // The last header's collapsed (0-width, hidden) menu-icon slot still lets its 26px button
        // spill past the cell, as does the resize handle at `right: -5px`. On the last column that
        // spill lands outside the grid and adds ~20px of horizontal scroll to a grid whose columns
        // fit. Clipping that one cell removes it; a revealed icon sits inside the cell anyway.
        tw`[& .MuiDataGrid-root .MuiDataGrid-columnHeader--last]:overflow-hidden`,
        // `.cls .MuiDataGrid-root` outranks the theme's bare `.MuiDataGrid-root`, so no `!important`.
        isStandalone && tw`[& .MuiDataGrid-root]:rounded-t-lg`,
        className,
      ]}
    >
      <GlobalStyles styles={GRID_COLUMN_MENU_STYLES} />
      <DataGridPro
        rows={rows}
        columns={columnsWithMinWidth}
        autoHeight={!fillHeight && autoHeight}
        loading={isLoading}
        pagination={!!paginationModel}
        paginationMode={!!paginationModel ? 'server' : undefined}
        paginationModel={paginationModel}
        pageSizeOptions={pageSizeOptions}
        rowHeight={44}
        columnHeaderHeight={36}
        sortingMode="server"
        disableRowSelectionOnClick
        disableColumnFilter
        columnVisibilityModel={columnVisibilityModel}
        pinnedColumns={pinnedColumns}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        onPinnedColumnsChange={onPinnedColumnsChange}
        onColumnOrderChange={onColumnOrderChange}
        {...props}
        slots={{
          noRowsOverlay: EmptyElement,
          noResultsOverlay: EmptyElement,
          columnSortedAscendingIcon: SortAscIcon,
          columnSortedDescendingIcon: SortDescIcon,
          columnUnsortedIcon: () => null,
          columnMenuSortAscendingIcon: SortAscIcon,
          columnMenuSortDescendingIcon: SortDescIcon,
          columnMenuPinRightIcon: PinIcon,
          columnMenuPinLeftIcon: PinIcon,
          columnMenuHideIcon: HideIcon,
          columnMenuManageColumnsIcon: ManageColumnsIcon,
          footer: () => (
            <GridTableFooter pageSizeOptions={pageSizeOptions} onExportClick={onExportClick} />
          ),
          ...(RowSlot ? { row: RowSlot } : {}),
        }}
        slotProps={{
          // Skeleton rows in the real column layout while loading, instead of a spinner over an
          // empty body; the skeleton also stands in for the empty state until the first load
          // settles, so an empty-state illustration never flashes before data.
          loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' },
          columnMenu: {
            slots: {
              columnMenuPinningItem: GridColumnMenuPinningItemWithUnpinIcon,
              columnMenuSortItem: GridColumnMenuSortItemWithUnsortIcon,
            },
            className: 'grid-column-menu-item',
          },
        }}
        css={[tw`bg-white border-0`, GRID_FOCUS_RING_STYLES]}
      />
    </div>
  )
}

export const GridTable = memo(GridTableComponent) as typeof GridTableComponent
